/**
 * CoreSettingsService
 * Loads/saves Copilot analysis configuration from SharePoint WVHealth_CoreSettings list.
 * Auto-creates the list item with defaults if it doesn't exist.
 */

import { getGraphClient, parseSiteUrl } from './sharePointService';
import { SEARCH_INDEX_STALE_DAYS } from '../config/constants';

export const SP_LIST_NAME_CORE_SETTINGS = 'WVHealth_CoreSettings';
export const CORE_SETTINGS_KEY = 'COPILOT_ANALYSIS_CONFIG';

export interface CopilotAnalysisConfig {
    modelId: string;
    cachingStrategy: 'Off' | 'Session' | 'Local';
    maxTotalIndexItems: number;
    readabilityClampMin: number;
    readabilityClampMax: number;
    readinessWeights: {
        structure: number;
        readability: number;
        base: number;
    };
    layoutThresholds: {
        warning: number;
        fail: number;
    };
    keywordStuffing: {
        ttrThreshold: number;
        ngramTopFrequency: number;
    };
    excludedFolderPaths: string[];
    assessPublishedOnly: boolean;
    searchIndexStaleDays: number;
}

export const DEFAULT_COPILOT_CONFIG: CopilotAnalysisConfig = {
    modelId: 'Snowflake/snowflake-arctic-embed-xs',
    cachingStrategy: 'Local',
    maxTotalIndexItems: 1000,
    readabilityClampMin: 40,
    readabilityClampMax: 90,
    readinessWeights: {
        structure: 0.5,
        readability: 0.3,
        base: 20
    },
    layoutThresholds: {
        warning: 0.19,
        fail: 0.25
    },
    keywordStuffing: {
        ttrThreshold: 0.4,
        ngramTopFrequency: 5
    },
    excludedFolderPaths: ['Templates'],
    assessPublishedOnly: true,
    searchIndexStaleDays: SEARCH_INDEX_STALE_DAYS,
};

interface SettingsRecord {
    spItemId: number | null;
    config: CopilotAnalysisConfig;
}

export class CoreSettingsService {
    private _cache: SettingsRecord | null = null;

    /**
     * Load settings from SharePoint.
     * If the list item does not exist, creates it with DEFAULT_COPILOT_CONFIG.
     */
    async getSettings(siteUrl: string): Promise<CopilotAnalysisConfig> {
        if (this._cache) return this._cache.config;

        try {
            const { siteId, listId } = await this._resolveIds(siteUrl);
            const existing = await this._fetchItem(siteId, listId);

            if (existing) {
                this._cache = existing;
                return existing.config;
            }

            // Not found — create with defaults
            console.log('[CoreSettings] No config found, creating with defaults...');
            const created = await this._createItem(siteId, listId, DEFAULT_COPILOT_CONFIG);
            this._cache = { spItemId: created, config: { ...DEFAULT_COPILOT_CONFIG } };
            return { ...DEFAULT_COPILOT_CONFIG };

        } catch (err) {
            console.warn('[CoreSettings] Failed to load from SP, using defaults:', err);
            return { ...DEFAULT_COPILOT_CONFIG };
        }
    }

    /**
     * Save settings back to SharePoint (PATCH if exists, POST if new).
     */
    async saveSettings(siteUrl: string, config: CopilotAnalysisConfig): Promise<void> {
        const { siteId, listId } = await this._resolveIds(siteUrl);

        const fields = {
            Title: CORE_SETTINGS_KEY,
            ConfigKey: CORE_SETTINGS_KEY,
            ConfigValue: JSON.stringify(config),
            UpdatedAt: new Date().toISOString()
        };

        const client = await getGraphClient();

        if (this._cache?.spItemId) {
            // PATCH existing
            await client
                .api(`/sites/${siteId}/lists/${listId}/items/${this._cache.spItemId}/fields`)
                .patch(fields);
        } else {
            // POST new
            const response = await client
                .api(`/sites/${siteId}/lists/${listId}/items`)
                .post({ fields });
            this._cache = { spItemId: response.id, config };
        }

        // Update cache
        if (this._cache) this._cache.config = config;
    }

    /** Clear in-memory cache (forces re-fetch from SP next call) */
    clearCache(): void {
        this._cache = null;
    }

    // ─── Internal helpers ──────────────────────────────────────────────────────

    private async _resolveIds(siteUrl: string): Promise<{ siteId: string; listId: string }> {
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);

        const site = await client.api(`/sites/${hostname}:${path}`).select('id').get();
        const siteId = site.id;

        // Try to get or create the list
        const lists = await client
            .api(`/sites/${siteId}/lists`)
            .filter(`displayName eq '${SP_LIST_NAME_CORE_SETTINGS}'`)
            .select('id')
            .get();

        if (lists.value && lists.value.length > 0) {
            return { siteId, listId: lists.value[0].id };
        }

        // List doesn't exist — create it
        console.log(`[CoreSettings] Creating list '${SP_LIST_NAME_CORE_SETTINGS}'...`);
        const newList = await client.api(`/sites/${siteId}/lists`).post({
            displayName: SP_LIST_NAME_CORE_SETTINGS,
            columns: [
                { name: 'ConfigKey', text: {} },
                { name: 'ConfigValue', text: { allowMultipleLines: true } },
                { name: 'UpdatedAt', dateTime: {} }
            ],
            list: { template: 'genericList' }
        });

        return { siteId, listId: newList.id };
    }

    private async _fetchItem(siteId: string, listId: string): Promise<SettingsRecord | null> {
        const client = await getGraphClient();
        const result = await client
            .api(`/sites/${siteId}/lists/${listId}/items`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .expand(`fields($select=id,ConfigKey,ConfigValue,UpdatedAt)`)
            .filter(`fields/ConfigKey eq '${CORE_SETTINGS_KEY}'`)
            .top(1)
            .get();

        if (!result.value || result.value.length === 0) return null;

        const item = result.value[0];
        let config: CopilotAnalysisConfig = { ...DEFAULT_COPILOT_CONFIG };
        try {
            const parsed = JSON.parse(item.fields.ConfigValue || '{}');
            config = { ...DEFAULT_COPILOT_CONFIG, ...parsed };
        } catch {
            console.warn('[CoreSettings] Failed to parse ConfigValue JSON, using defaults');
        }

        return { spItemId: Number(item.id), config };
    }

    private async _createItem(siteId: string, listId: string, config: CopilotAnalysisConfig): Promise<number> {
        const client = await getGraphClient();
        const response = await client
            .api(`/sites/${siteId}/lists/${listId}/items`)
            .post({
                fields: {
                    Title: CORE_SETTINGS_KEY,
                    ConfigKey: CORE_SETTINGS_KEY,
                    ConfigValue: JSON.stringify(config),
                    UpdatedAt: new Date().toISOString()
                }
            });
        return Number(response.id);
    }
}

// Singleton for app-wide use
export const coreSettingsService = new CoreSettingsService();
